import React from "react";

import { FormProvider, useForm } from "react-hook-form";

import { useConnection } from "src/api/connectApi/queries";
import { Connection } from "src/api/connectApi/types";
import { useWorkspace } from "src/api/queries";
import { ErrorBaseInfo, FieldErrors } from "src/api/types";
import { Card } from "src/base-components/Card";
import { LoadingView } from "src/base-components/LoadingView";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import {
  CustomConnectionNode,
  CustomConnectionNodeData,
} from "src/constants/NodeDataTypes";
import { BodyEditor } from "src/customConnectionNode/editorComponents/BodyEditor";
import { ConfigPane } from "src/customConnectionNode/editorComponents/ConfigPane";
import { FileExtractionEditor } from "src/customConnectionNode/editorComponents/FileExtractionEditor";
import { KeyValueArrayEditor } from "src/customConnectionNode/editorComponents/KeyValueArrayEditor";
import { ResponseMapping } from "src/customConnectionNode/editorComponents/ResponseMapping";
import { VerbPathSelector } from "src/customConnectionNode/editorComponents/VerbPathSelector";
import { CustomConnectionForm } from "src/customConnectionNode/types";
import { isBodyEmpty } from "src/customConnectionNode/utils";
import { Accordion } from "src/design-system/Accordion";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { convertFieldErrorsBeToFe } from "src/utils/FieldErrorUtils";
import { useSubmitForm } from "src/utils/useSubmitForm";

type WrapperProps = {
  node: CustomConnectionNode;
  workspaceId: string;
} & NodeEditorBaseProps<CustomConnectionNodeData>;

export const CustomConnectionNodeEditor: React.FC<WrapperProps> = ({
  immutable,
  node,
  workspaceId,
  displayedError,
  isReactive,
  onUpdate,
}) => {
  const workspace = useWorkspace(workspaceId);
  const connection = useConnection({
    baseUrl: workspace.data?.base_url,
    id: node.data.connectionId,
  });
  return (
    <LoadingView
      queryResult={connection}
      renderUpdated={(connection: Connection) => (
        <CustomConnectionNodeEditorInternal
          connection={connection}
          displayedError={displayedError}
          immutable={immutable}
          isReactive={isReactive}
          node={node}
          onUpdate={onUpdate}
        />
      )}
    />
  );
};

type Props = {
  connection: Connection;
  immutable: boolean;
  node: CustomConnectionNode;
  displayedError: ErrorBaseInfo | undefined;
} & NodeEditorBaseProps<CustomConnectionNodeData>;

export const CustomConnectionNodeEditorInternal: React.FC<Props> = ({
  connection,
  immutable,
  node,
  displayedError,
  isReactive,
  onUpdate,
}) => {
  const { renderedInDiffView } = useDiffViewContext();
  const currentFormValues: CustomConnectionForm = {
    verb: node.data.verb,
    path: node.data.path,
    params: node.data.params,
    headers: node.data.headers,
    body: node.data.body,
    response: node.data.response,
    config: node.data.config,
  };

  const formProps = useForm<CustomConnectionForm>({
    defaultValues: currentFormValues,
    ...(isReactive && { values: currentFormValues }),
  });

  const paramsEmpty = currentFormValues.params.length === 0;
  const headersEmpty = currentFormValues.headers.length === 0;
  const bodyEmpty = isBodyEmpty(currentFormValues.body, currentFormValues.verb);
  const isConfigurationEmpty = paramsEmpty && headersEmpty && bodyEmpty;
  const filesEmpty =
    !currentFormValues.config.file_extraction_config?.active &&
    (currentFormValues.config.file_extraction_config?.extractions.length ??
      0) === 0;

  const shouldShowParams = isConfigurationEmpty || !paramsEmpty;
  const shouldShowHeaders = isConfigurationEmpty || !headersEmpty;
  const shouldShowBody = isConfigurationEmpty || !bodyEmpty;
  const shouldShowFiles = !filesEmpty;

  useSubmitForm({
    onChange: (data: CustomConnectionForm) => {
      onUpdate({ newData: data });
    },
    disabled: isReactive,
    previousValues: currentFormValues,
    watch: formProps.watch,
  });

  const runFieldErrors: FieldErrors | undefined = displayedError?.field_errors
    ? convertFieldErrorsBeToFe(displayedError.field_errors)
    : undefined;

  const bodyDisablingVerbs = new Set(["get", "head", "options", undefined]);
  const isPowertoolsFlagEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.powertools,
  );

  return (
    <FormProvider {...formProps}>
      <VerbPathSelector
        connection={connection}
        immutable={immutable}
        runFieldErrors={runFieldErrors}
      />
      <Accordion
        defaultValue={
          [
            shouldShowParams && "parameters",
            shouldShowHeaders && "headers",
            shouldShowBody && "body",
            "response",
            shouldShowFiles && "files",
            "config",
          ].filter(Boolean) as string[]
        }
      >
        <Accordion.Item
          dataLoc="cc-node-query-parameters"
          isReadOnly={immutable || renderedInDiffView}
          label="Query parameters"
          value="parameters"
        >
          <Card className="w-full">
            <KeyValueArrayEditor
              addButtonText="Add parameter"
              fieldName="params"
              immutable={immutable}
              runFieldErrors={runFieldErrors}
            />
          </Card>
        </Accordion.Item>
        <Accordion.Item
          dataLoc="cc-node-headers"
          isReadOnly={immutable || renderedInDiffView}
          label="Headers"
          value="headers"
        >
          <Card className="w-full">
            <KeyValueArrayEditor
              addButtonText="Add header"
              fieldName="headers"
              immutable={immutable}
              runFieldErrors={runFieldErrors}
            />
          </Card>
        </Accordion.Item>
        {!bodyDisablingVerbs.has(formProps.getValues().verb) && (
          <Accordion.Item
            dataLoc="cc-node-body"
            isReadOnly={immutable || renderedInDiffView}
            label="Body"
            value="body"
          >
            <BodyEditor
              connectionId={node.data.connectionId}
              immutable={immutable}
              runFieldErrors={runFieldErrors}
            />
          </Accordion.Item>
        )}
        <Accordion.Item
          isReadOnly={immutable || renderedInDiffView}
          label="Configure API response"
          value="response"
        >
          <ResponseMapping
            immutable={immutable}
            runFieldErrors={runFieldErrors}
          />
        </Accordion.Item>
        {isPowertoolsFlagEnabled && (
          <Accordion.Item
            dataLoc="cc-node-files"
            isReadOnly={immutable || renderedInDiffView}
            label="File extraction"
            value="files"
          >
            <FileExtractionEditor
              immutable={immutable}
              runFieldErrors={runFieldErrors}
            />
          </Accordion.Item>
        )}
        <Accordion.Item
          isReadOnly={immutable || renderedInDiffView}
          label="Advanced settings"
          value="config"
        >
          <ConfigPane connection={connection} immutable={immutable} />
        </Accordion.Item>
      </Accordion>
    </FormProvider>
  );
};
